/**
 * CRA3D Resume Studio — landing page (list of the user's resumes).
 *
 * Phase 2 surface: shows a card per saved resume with rename / archive
 * / delete controls, plus a "New resume" CTA that opens
 * `/resume-studio/new`. Downloads / preview are Phase 5 and are not
 * shown here.
 *
 * Free-vs-paid honest copy (Mark's decision #9): the top-of-page notice
 * spells out that building and previewing is free and downloads will
 * require a purchase later. No prices are shown.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { ResumeStudioConsentModal } from "@/components/ResumeStudioConsentModal";
import {
  FileText, Plus, Trash2, Archive, ArchiveRestore, PenLine, ShieldCheck,
} from "lucide-react";

const TYPE_LABEL = {
  targeted_ats:              "Targeted (ATS)",
  career_repath:             "Career Repath",
  traditional_chronological: "Chronological",
  skills_first:              "Skills-first",
};

export default function ResumeStudioList() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const [{ data: st }, { data: rl }] = await Promise.all([
        api.get("/resume-studio/status"),
        api.get("/resume-studio/resumes"),
      ]);
      setStatus(st);
      setResumes(rl.resumes || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) {
        setError(typeof detail === "string" ? detail : "Not on your plan.");
      } else {
        setError(extractApiError(err, "Failed to load your resumes."));
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    if (!status?.consent_on_file?.present) {
      setShowConsent(true);
      return;
    }
    navigate("/resume-studio/new");
  };

  const onConsented = async () => {
    setShowConsent(false);
    await load();
    toast.success("Consent recorded. You can now create resumes.");
    navigate("/resume-studio/new");
  };

  const toggleArchive = async (row) => {
    try {
      const next = row.status === "archived" ? "draft" : "archived";
      await api.patch(`/resume-studio/resumes/${row.id}`, { status: next });
      toast.success(next === "archived" ? "Archived." : "Restored to drafts.");
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Update failed."));
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeletingBusy(true);
    try {
      await api.delete(`/resume-studio/resumes/${pendingDelete.id}`);
      toast.success("Resume permanently deleted.");
      setPendingDelete(null);
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Delete failed."));
    } finally {
      setDeletingBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <ResumeStudioConsentModal
          open={showConsent}
          onConsented={onConsented}
          onCancel={() => setShowConsent(false)}
        />

        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-fg-secondary">
              Resume Studio
            </p>
            <h1
              data-testid="resume-studio-title"
              className="mt-1 text-3xl sm:text-4xl font-heading font-medium text-fg-primary flex items-center gap-2"
            >
              <FileText className="h-6 w-6" aria-hidden="true" />
              Your resumes
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-fg-secondary">
              Build a truthful, editable resume from information you've
              approved inside CRA3D or that you type in directly. Every
              claim on every section carries a provenance tag so you can
              see exactly where each line came from.
            </p>
          </div>
          <Button
            onClick={startNew}
            data-testid="resume-studio-new-btn"
            className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
          >
            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
            New resume
          </Button>
        </div>

        <div
          data-testid="resume-studio-free-vs-paid"
          className="mb-6 rounded-md border border-progress-teal/30 bg-progress-teal/5 p-3"
        >
          <p className="text-xs text-fg-primary flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 flex-shrink-0 text-progress-teal mt-0.5"
                          aria-hidden="true" />
            <span>
              <strong>Building and previewing your resume is free.</strong>{" "}
              Downloading a finished resume (PDF / DOCX / plain text) will
              require a purchase later — we'll show clear pricing before you
              upgrade. No auto-charges. No surprise fees.
            </span>
          </p>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="resume-studio-error"
                  className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <p className="text-sm text-fg-secondary py-12 text-center"
             data-testid="resume-studio-loading">
            Loading…
          </p>
        ) : resumes.length === 0 ? (
          <div
            data-testid="resume-studio-empty"
            className="rounded-lg border border-border-subtle bg-bg-surface p-8 text-center"
          >
            <p className="text-sm text-fg-secondary">
              No resumes yet. Start one from a blank template, or import
              approved information from your CRA3D profile.
            </p>
            <Button
              onClick={startNew}
              size="sm"
              data-testid="resume-studio-empty-new-btn"
              className="mt-4 bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
              New resume
            </Button>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="resume-studio-list">
            {resumes.map((r) => (
              <li
                key={r.id}
                data-testid={`resume-row-${r.id}`}
                className="rounded-lg border border-border-subtle bg-bg-surface p-4 flex flex-wrap items-center justify-between gap-3"
              >
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-fg-secondary">
                    {TYPE_LABEL[r.resume_type] || r.resume_type}
                    {r.status === "archived" && " · archived"}
                  </p>
                  <p className="mt-1 text-lg font-heading font-medium text-fg-primary"
                     data-testid={`resume-row-${r.id}-name`}>
                    {r.name}
                  </p>
                  <p className="mt-1 text-[11px] text-fg-muted">
                    Updated {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/resume-studio/${r.id}`}>
                    <Button
                      size="sm"
                      data-testid={`resume-row-${r.id}-open-btn`}
                      className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                    >
                      <PenLine className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                      Open
                    </Button>
                  </Link>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleArchive(r)}
                    data-testid={`resume-row-${r.id}-archive-btn`}
                    className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                  >
                    {r.status === "archived" ? (
                      <>
                        <ArchiveRestore className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                        Restore
                      </>
                    ) : (
                      <>
                        <Archive className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                        Archive
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPendingDelete(r)}
                    data-testid={`resume-row-${r.id}-delete-btn`}
                    className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={!!pendingDelete} onOpenChange={(v) => { if (!v) setPendingDelete(null); }}>
        <DialogContent
          data-testid="resume-studio-delete-dialog"
          className="bg-bg-surface border-border-subtle text-fg-primary"
        >
          <DialogHeader>
            <DialogTitle className="font-heading">Delete this resume?</DialogTitle>
            <DialogDescription className="text-fg-secondary">
              Permanent. Every version and every claim on this resume will
              be removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2 sm:justify-end">
            <Button
              variant="ghost"
              onClick={() => setPendingDelete(null)}
              data-testid="resume-studio-delete-cancel-btn"
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              Keep it
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deletingBusy}
              data-testid="resume-studio-delete-confirm-btn"
            >
              {deletingBusy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
